import { motion, useMotionValue, useSpring } from "framer-motion";
import { useRef } from "react";

export const EASE = [0.22, 1, 0.36, 1];

export function Reveal({ children, delay = 0, y = 28, className = "", as = "div", ...rest }) {
  const M = motion[as] || motion.div;
  return (
    <M
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.12 }}
      transition={{ duration: 0.75, delay, ease: EASE }}
      {...rest}
    >
      {children}
    </M>
  );
}

// Masked line-by-line reveal. onLoad=true animates immediately (hero), else in view.
export function MaskLines({ lines, className = "", lineClass = "", delay = 0, onLoad = false, as = "h2", testId }) {
  const Tag = as;
  const trigger = onLoad ? { animate: "show" } : { whileInView: "show", viewport: { once: true, amount: 0.1 } };
  return (
    <Tag className={className} data-testid={testId}>
      <motion.span className="block" initial="hide" {...trigger}
        variants={{ show: { transition: { staggerChildren: 0.11, delayChildren: delay } } }}>
        {lines.map((line, i) => (
          <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
            <motion.span className={`block will-change-transform ${lineClass}`}
              variants={{ hide: { y: "110%", rotate: 2 }, show: { y: "0%", rotate: 0, transition: { duration: 0.95, ease: EASE } } }}>
              {line}
            </motion.span>
          </span>
        ))}
      </motion.span>
    </Tag>
  );
}

export function Stagger({ children, className = "", gap = 0.08 }) {
  return (
    <motion.div className={className} initial="hide" whileInView="show" viewport={{ once: true, amount: 0.1 }}
      variants={{ show: { transition: { staggerChildren: gap } } }}>
      {children}
    </motion.div>
  );
}

export const itemVariants = {
  hide: { opacity: 0, y: 26 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
};

// Very limited pointer tilt for cards (desktop pointers only).
export function Tilt({ children, className = "", max = 4, ...rest }) {
  const ref = useRef(null);
  const rx = useSpring(useMotionValue(0), { stiffness: 160, damping: 18 });
  const ry = useSpring(useMotionValue(0), { stiffness: 160, damping: 18 });
  const onMove = (e) => {
    if (e.pointerType !== "mouse" || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * max * 2);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * max * 2);
    ref.current.style.setProperty("--mx", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  const reset = () => { rx.set(0); ry.set(0); };
  return (
    <motion.div ref={ref} onPointerMove={onMove} onPointerLeave={reset}
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 1000 }} className={className} {...rest}>
      {children}
    </motion.div>
  );
}

export function Magnetic({ children, strength = 0.25 }) {
  const ref = useRef(null);
  const x = useSpring(useMotionValue(0), { stiffness: 200, damping: 15 });
  const y = useSpring(useMotionValue(0), { stiffness: 200, damping: 15 });
  const onMove = (e) => {
    if (e.pointerType !== "mouse" || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - r.left - r.width / 2) * strength);
    y.set((e.clientY - r.top - r.height / 2) * strength);
  };
  return (
    <motion.span ref={ref} className="inline-block" style={{ x, y }} onPointerMove={onMove}
      onPointerLeave={() => { x.set(0); y.set(0); }}>
      {children}
    </motion.span>
  );
}
